import React, { useState } from 'react';
import Button from '../../../shared/components/Button';
import { formatCurrency } from '../../../shared/utils/formatCurrency';

// COPY: provisional — money-flow model choice (Stage 24 / Phase C1). Foundation
// only; nothing branches on it yet. Default is through_admin (current behavior).
const MONEY_FLOW_MODELS = [
  {
    value: 'through_admin',
    label: 'Members pay the admin',
    description: 'Members pay you (the admin). You record each payment and send the collector their payout.',
  },
  {
    value: 'direct_to_collector',
    label: 'Members pay the collector directly',
    description: "Members pay that round's collector directly and submit proof; the collector confirms receipt. No admin payout step.",
  },
];

const ContributionDetails = ({ data, onNext, onBack }) => {
  const [amount, setAmount] = useState(data?.amount || '');
  const [frequency, setFrequency] = useState(data?.frequency || '');
  const [moneyFlowModel, setMoneyFlowModel] = useState(data?.moneyFlowModel || 'through_admin');
  const [errors, setErrors] = useState({});

  const frequencies = [
    {
      value: 'weekly',
      label: 'Weekly',
      description: 'Contributions due every 7 days',
      example: 'Repeats weekly from your first collection date'
    },
    {
      value: 'bi-weekly',
      label: 'Bi-weekly',
      description: 'Contributions due every 14 days',
      example: 'Repeats every 2 weeks from your first collection date'
    },
    {
      value: 'monthly',
      label: 'Monthly',
      description: 'Contributions due once per month',
      example: 'Repeats monthly from your first collection date'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!frequency) {
      newErrors.frequency = 'Please select a contribution frequency';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onNext({
        amount: parseFloat(amount),
        frequency,
        moneyFlowModel,
      });
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const selectedFrequency = frequencies.find(f => f.value === frequency);

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
          Set contribution details
        </h1>
        <p className="text-neutral-600">
          Define how much and how often members will contribute
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 space-y-6">
        {/* Contribution Amount */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-neutral-700 mb-2">
            Contribution amount per hand *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-neutral-500 text-lg">$</span>
            <input
              type="text"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0.00"
              className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg ${
                errors.amount ? 'border-error-500' : 'border-neutral-300'
              }`}
            />
          </div>
          {errors.amount && (
            <p className="mt-1 text-sm text-error-600">{errors.amount}</p>
          )}
          
          {/* Amount Preview */}
          {amount && parseFloat(amount) > 0 && (
            <div className="mt-2 p-3 bg-success-50 rounded-lg">
              <p className="text-sm text-success-800">
                Each hand will contribute: <strong>{formatCurrency(parseFloat(amount))}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Contribution Frequency */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            Contribution frequency *
          </label>
          <div className="space-y-3">
            {frequencies.map(freq => {
              const isSelected = frequency === freq.value;
              return (
              <div
                key={freq.value}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
                onClick={() => setFrequency(freq.value)}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="frequency"
                    value={freq.value}
                    checked={isSelected}
                    onChange={() => setFrequency(freq.value)}
                    className="mr-3 text-primary-600 focus:ring-primary-500"
                  />
                  {/* Selected card uses the light primary-50 fill, so switch the text
                      to dark primary tones for contrast (neutral-900 is near-white in
                      this theme and washes out on the light fill). */}
                  <div className="flex-1">
                    <div className={`font-medium ${isSelected ? 'text-primary-900' : 'text-neutral-900'}`}>{freq.label}</div>
                    <div className={`text-sm mt-1 ${isSelected ? 'text-primary-800' : 'text-neutral-600'}`}>{freq.description}</div>
                    <div className={`text-xs mt-1 ${isSelected ? 'text-primary-700' : 'text-neutral-500'}`}>{freq.example}</div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          {errors.frequency && (
            <p className="mt-1 text-sm text-error-600">{errors.frequency}</p>
          )}
        </div>

        {/* Money-flow model (Stage 24 / Phase C1) */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">How do members pay?</label>
          <p className="text-xs text-neutral-500 mb-3">You can change this later, but only before your first cycle or between cycles.</p>
          <div className="space-y-3">
            {MONEY_FLOW_MODELS.map(m => {
              const isSelected = moneyFlowModel === m.value;
              return (
                <div
                  key={m.value}
                  onClick={() => setMoneyFlowModel(m.value)}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-neutral-300 hover:border-neutral-400'}`}
                >
                  <div className="flex items-start">
                    <input type="radio" name="moneyFlowModel" value={m.value} checked={isSelected} onChange={() => setMoneyFlowModel(m.value)} className="mr-3 mt-1 text-primary-600 focus:ring-primary-500" />
                    <div className="flex-1">
                      <div className={`font-medium ${isSelected ? 'text-primary-900' : 'text-neutral-900'}`}>{m.label}</div>
                      <div className={`text-sm mt-1 ${isSelected ? 'text-primary-800' : 'text-neutral-600'}`}>{m.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Frequency Explanation */}
        {selectedFrequency && (
          <div className="mt-6 p-4 bg-primary-50 rounded-lg">
            <h3 className="text-sm font-medium text-primary-900 mb-2">
              How {selectedFrequency.label.toLowerCase()} contributions work
            </h3>
            <p className="text-sm text-primary-800">
              {selectedFrequency.description}. Each member will contribute the same amount 
              {selectedFrequency.value === 'weekly' && ' every week on the same day.'}
              {selectedFrequency.value === 'bi-weekly' && ' every two weeks on the same day.'}
              {selectedFrequency.value === 'monthly' && ' once per month on the scheduled date.'}
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex space-x-3 mt-8">
        <Button
          onClick={onBack}
          variant="secondary"
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          className="flex-1"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ContributionDetails;
